import { i18n } from 'i18next';
import { isNil } from 'lodash';
import { getProvinceAbbreviation } from '~domain/helper/address-helper';
import { Province } from '~domain/model/core/address/province';
import { HomeLine } from '~domain/model/core/home-line';
import { LanguageId, SupportedLanguageElement, SupportedLanguages } from '~domain/model/core/supported-languages';

export type TranslatorFn = (key: string) => string;

type FillerType = string | number;

type TlOptions = {
  /**
   * Interpolation
   * @see https://www.i18next.com/translation-function/interpolation
   *
   * - Interpolate tokens in the translations with given values
   * - Array format:
   * ```ts
   * const dict = {
   *   baseId: "My name is {{0}} and I like {{1}}."
   * };
   *
   * translator.tl("baseId", { fillers: ["Bob", "apples"] }); // "My name is Bob and I like apples."
   * ```
   * - Object format:
   * ```ts
   * const dict = {
   *   baseId: "My name is {{name}} and I like {{fruit}}."
   * };
   *
   * translator.tl("baseId", { fillers: { name: "Bob", fruit: "apples" } }); // "My name is Bob and I like apples."
   * ```
   */
  fillers?: FillerType[] | Record<string, FillerType>;

  /**
   * Pluralization
   * @see https://www.i18next.com/translation-function/plurals
   *
   * - If `count` is 0, uses "baseId_zero" id. Fallbacks to the language-specific prefix if not defined.
   *   For example, 0 is singular in french and plural in english.
   * - If `count` is 1, uses the "baseId_one" id
   * - If `count` is more than 1, uses "baseId_other" id
   * - Can be combined with `context`, the `context` comes first. Ex.: "baseId_context_other"
   * - The `count` value is available for interpolation
   *
   * ```ts
   * const dict = {
   *   "foo_one": "There is {{count}} foo.",
   *   "foo_other": "There are {{count}} foos."
   * }
   *
   * translator.tl('foo', { count: 1 }); // "There is 1 foo."
   * ```
   */
  count?: number;

  /**
   * Context-dependant translations
   * @see https://www.i18next.com/translation-function/context
   *
   * - Define a context for a given id, with fallback.
   * - Can be combined with `count`, the `context` comes first. Ex.: "baseId_context_other"
   *
   * ```ts
   * const dict = {
   *   "foo_blue": "This color makes me relaxed.",
   *   "foo_red": "This color makes me angry."
   * }
   *
   * translator.tl('foo', { context: 'blue' }); // "This color makes me relaxed."
   * ```
   */
  context?: string;
};

// Translator class that abstracts usage of the i18next library.
// All methods are arrow functions to ensure that `this` is defined when used on frontend events
export class Translator {
  // I18n private properties to execute different translation logic
  readonly i18n: i18n;

  private i18nTranslator: <T>(value: string, mapper?: T) => string;

  // Returns true if value exists
  readonly tExists: (value: string) => boolean;

  constructor(i18n: i18n, i18nTranslator: TranslatorFn, tExists: (value: string) => boolean) {
    this.i18n = i18n;
    this.i18nTranslator = i18nTranslator;
    this.tExists = tExists;
  }

  // Change language to English or French
  changeTranslatorLanguage = async (id: string) => {
    // i18nLib.changeLanguage changes the local storage i18nextLng variable
    await this.i18n.changeLanguage(id);
  };

  // If current language ID is supported, return it. Otherwise, return default language ID.
  getCurrentLanguageId = (): LanguageId => {
    if (this.isLanguageSupported(this.i18n.language)) {
      return this.i18n.language as LanguageId;
    } else {
      return SupportedLanguages[0].id;
    }
  };

  // Only 2 languages for now, default is english
  isDefaultLanguage = (): boolean => {
    if (this.getCurrentLanguageId() === SupportedLanguages[0].id) return true;

    return false;
  };

  // Return true if language ID passed in the parameters is supported by us
  isLanguageSupported = (languageId: string): boolean => {
    const result: SupportedLanguageElement[] = SupportedLanguages.filter((lng) => lng.id === languageId);

    if (result.length === 0) return false;

    return true;
  };

  /**
   * General purpose translation function. Supports interpolation, pluralization and context.
   */
  tl = (id: string, options?: TlOptions): string => {
    const i18nOptions: any = {};

    if (!isNil(options?.fillers)) {
      i18nOptions.replace = Array.isArray(options.fillers)
        ? // If fillers is an array, convert it to a map
          options.fillers.reduce((acc, value, index) => {
            acc[index] = value;
            return acc;
          }, {})
        : // Otherwise use as-is
          options.fillers;
    }

    if (!isNil(options?.count)) {
      i18nOptions.count = options.count;
    }

    if (!isNil(options?.context)) {
      i18nOptions.context = options.context;
    }

    return this.i18nTranslator(id, i18nOptions);
  };

  /**
   * @deprecated use tl instead
   * Create translated text with specified fillers
   */
  m = (id: string, fillers: (string | number)[]) => {
    const mapper = {};
    fillers.forEach((value: string, index: number) => (mapper[index] = value));
    return this.i18nTranslator(id, mapper);
  };

  /**
   * Create translated text with fillers when text depends on the province and/or the home line
   * TODO: add this feature in tl options and deprecate this method
   */
  ms = (id: string, fillers: (string | number)[], province: Province, homeLine?: HomeLine) => {
    const mapper = {};
    fillers.forEach((value: string, index: number) => (mapper[index] = value));
    return this.i18nTranslator(this.getIdByProvince(id, province, homeLine), mapper);
  };

  /**
   * @deprecated use tl instead
   * Translate an accessor id to text
   */
  t = (id: string): string => {
    if (isNil(this.i18nTranslator)) return;
    return this.i18nTranslator(id);
  };

  /**
   * Create translated text when text depends on the province and/or the home line
   * TODO: add this feature in tl options and deprecate this method
   */
  ts = (id: string, province: Province, homeLine?: HomeLine): string => {
    if (isNil(this.i18nTranslator)) return;
    return this.i18nTranslator(this.getIdByProvince(id, province, homeLine));
  };

  /** Returns if a translation exists when text depends on the province and/or the home line */
  public tsExists = (id: string, province: Province, homeLine?: HomeLine): boolean => {
    return this.tExists(this.getIdByProvince(id, province, homeLine));
  };

  /** Returns the translation id to use */
  private getIdByProvince = (id: string, province: Province, homeLine?: HomeLine): string => {
    const provinceAccessor: string = getProvinceAbbreviation(province);

    // The most restrictive is translationId.homeLine.province
    if (this.tExists(`${id}.${homeLine}.${provinceAccessor}`)) {
      return `${id}.${homeLine}.${provinceAccessor}`;
    }

    // If the most restrictive pattern does not exists, try with only home line (translationId.homeLine)
    if (this.tExists(`${id}.${homeLine}`)) {
      return `${id}.${homeLine}`;
    }

    // If the most restrictive pattern does not exists, try with only province (translationId.province)
    if (this.tExists(`${id}.${provinceAccessor}`)) {
      return `${id}.${provinceAccessor}`;
    }

    // If none of the restrictive patterns exists, try to use the base translation id
    return id;
  };
}
